// Relative so this module stays resolvable under jest, which (unlike webpack)
// has no moduleNameMapper for the `@/` alias. The API type is a type-only
// import, so it is erased at compile time and never loaded at runtime.
import { v4 as uuidV4 } from "uuid";
import { calculateMatchScore } from "../strings/fuzzyMatch";
import type { LinkedOrderId } from "../api/risk/brokerage/tt/types";

/**
 * The four free-text fields TT orders carry, in ranking priority order.
 * `text_a` is the strongest signal; `text_tt` the weakest.
 */
const TEXT_FIELDS = ["text_a", "text_b", "text_c", "text_tt"] as const;

type TextField = (typeof TEXT_FIELDS)[number];

/** The text fields of the order being edited, used as the comparison reference. */
export type OrderTextFields = Record<TextField, string | null>;

/** The shape the picker needs from an already-fetched order row. */
export interface LinkableOrder extends OrderTextFields {
  guid_order_id: string;
  instrument_name: string | null;
}

/** True when both sides carry text and are the same, ignoring case and padding. */
function isSameText(candidate: string | null, reference: string | null): boolean {
  const left = candidate?.trim().toLowerCase() ?? "";
  const right = reference?.trim().toLowerCase() ?? "";
  return left !== "" && left === right;
}

/** Similarity of one reference field to one candidate field, 0 when either is blank. */
function similarity(reference: string | null, candidate: string | null): number {
  if (!reference?.trim() || !candidate?.trim()) return 0;
  return calculateMatchScore(reference, candidate);
}

/**
 * Sort key for one candidate, highest value wins position by position:
 *  1. `text_a` exactly equals the reference — these float to the top.
 *  2. exact matches on `text_b`, then `text_c`, then `text_tt`.
 *  3. fuzzy similarity on `text_a`, then `text_b`, `text_c`, `text_tt`.
 *
 * A blank reference field never matches: with no `text_a` on the order being
 * edited there is nothing to rank against, so ordering falls through to the
 * remaining fields.
 */
function sortKey(order: LinkableOrder, reference: OrderTextFields): number[] {
  return [
    ...TEXT_FIELDS.map((field) => (isSameText(order[field], reference[field]) ? 1 : 0)),
    ...TEXT_FIELDS.map((field) => similarity(reference[field], order[field])),
  ];
}

/** Descending comparison, position by position. Ties keep the fetched order. */
function compareKeys(left: number[], right: number[]): number {
  for (let i = 0; i < left.length; i++) {
    const difference = right[i] - left[i];
    if (difference !== 0) return difference;
  }
  return 0;
}

/**
 * Orders offered as link targets, best match first: the order being edited is
 * excluded, orders sharing its `text_a` come next, and the rest follow by how
 * similar their text fields are. Ties keep the fetched order.
 */
export function rankLinkedOrderCandidates<T extends LinkableOrder>(
  currentGuidOrderId: string,
  reference: OrderTextFields,
  orders: readonly T[],
): T[] {
  return orders
    .filter((order) => order.guid_order_id !== currentGuidOrderId)
    .map((order) => ({ order, key: sortKey(order, reference) }))
    .sort((left, right) => compareKeys(left.key, right.key))
    .map((entry) => entry.order);
}

/**
 * Label for one picker option: the fields the ranking is based on, so the user
 * can see why an order was floated to the top.
 */
export function linkedOrderOptionLabel(order: LinkableOrder): string {
  const parts = [
    order.instrument_name,
    order.text_a,
    order.text_b,
    order.text_c,
    order.text_tt,
  ]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(" · ") : order.guid_order_id;
}

/**
 * Build the linked-order payload. Every distinct instrument gets one subgroup;
 * the current order and linked orders on the same instrument share its id.
 * Current assumption is we can guess the sub groups without the user having to state them
 * And amendments are broken out by different instrument names
 * e.g. Dec 26 vs Mar 27 for rolls
 * e.g. 460x490x550 vs 460x470x580 for leg changes etc
 */
export function buildLinkedOrderGrouping({
  enabled,
  linkedGuidOrderIds,
  candidates,
  currentInstrumentName,
}: {
  enabled: boolean;
  /** Every chosen `guid_order_id`, empty rows included. */
  linkedGuidOrderIds: readonly string[];
  candidates: readonly LinkableOrder[];
  currentInstrumentName: string | null;
}): {} | { sub_group_id?: string; linked_order_ids?: LinkedOrderId[] } {
  if (!enabled) return {};

  const guids = [...new Set(linkedGuidOrderIds.filter((guid) => !!guid))];
  if (guids.length === 0) return {};

  const byGuid = new Map(candidates.map((candidate) => [candidate.guid_order_id, candidate]));
  const subGroupIds = new Map<string | null, string>();
  const subGroupIdFor = (instrumentName: string | null): string => {
    const existing = subGroupIds.get(instrumentName);
    if (existing) return existing;
    const created = uuidV4();
    subGroupIds.set(instrumentName, created);
    return created;
  };

  return {
    sub_group_id: subGroupIdFor(currentInstrumentName),
    linked_order_ids: guids.map((guid) => ({
      guid_order_id: guid,
      sub_group_id: subGroupIdFor(byGuid.get(guid)?.instrument_name ?? null),
    })),
  };
}

/**
 * Candidates one link row may offer: everything except the orders already
 * chosen in the *other* rows, so an order can only be linked once. The row's
 * own choice is deliberately still returned, otherwise the picker could not
 * render the label of what it currently holds.
 */
export function linkRowCandidates<T extends LinkableOrder>(
  candidates: readonly T[],
  chosenByOtherRows: readonly string[],
): T[] {
  const taken = new Set(chosenByOtherRows.filter((guid) => !!guid));
  return candidates.filter((candidate) => !taken.has(candidate.guid_order_id));
}
